import { isTMA, retrieveLaunchParams, retrieveRawInitData } from '@telegram-apps/sdk-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { ROUTES } from '~/constants';
import { env } from '~/env/client';
import { packsControllerFindPackByPartnerIds } from '~/lib/api';
import { parseStartParam } from '~/lib/utils';

interface UseTransferDeeplinkOptions {
  /**
   * Enable browser testing mode - allows testing transfer deeplinks
   * by setting query parameters: ?startParam=col_1_pac_2_ins_3_mid_8071574253
   */
  enableBrowserTesting?: boolean;
}

/**
 * Hook to handle transfer deeplink navigation
 * Extracts transfer deeplink logic from auth provider for reusability and testability
 *
 * Features:
 * - Handles transfer deeplinks in Telegram environment
 * - Supports browser testing mode via query parameters
 * - Prevents duplicate processing
 * - Automatically redirects to my-collections with transfer params
 */
export const useTransferDeeplink = (options: UseTransferDeeplinkOptions = {}) => {
  const { enableBrowserTesting = false } = options;
  const router = useRouter();
  const [isHandlingTransfer, setIsHandlingTransfer] = useState(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Skip if already processed
    if (hasProcessedRef.current) {
      return;
    }

    const handleTransfer = async () => {
      let startParameter: string | undefined;
      let telegramUserId: number | undefined;

      // Get parameters based on environment
      if (isTMA()) {
        // Telegram environment
        const params = retrieveLaunchParams(true);
        startParameter = params.tgWebAppStartParam;
        telegramUserId = params.tgWebAppData?.user?.id;
      } else if (enableBrowserTesting && typeof window !== 'undefined') {
        // Browser testing mode
        const useDirectToken = env.NEXT_PUBLIC_USE_DIRECT_TOKEN === 'true';

        // Only allow browser testing when direct token mode is enabled
        if (!useDirectToken) {
          return;
        }

        const searchParameters = new URLSearchParams(window.location.search);
        startParameter = searchParameters.get('startParam') || undefined;

        // For browser testing, we need a test telegram user ID
        // In direct token mode, we can use a test ID
        const testUserIdString = searchParameters.get('testTelegramUserId');
        if (testUserIdString) {
          telegramUserId = Number.parseInt(testUserIdString, 10);
        }
      } else {
        return;
      }

      // Parse the start parameter
      const parsed = parseStartParam(startParameter);

      // Only handle transfer type
      if (parsed?.type !== 'transfer') {
        return;
      }

      // Check for required data
      const rawInitData = isTMA() ? retrieveRawInitData() : undefined;
      if (isTMA() && !rawInitData) {
        return;
      }

      if (!telegramUserId) {
        return;
      }

      // Mark as processing to prevent duplicates
      setIsHandlingTransfer(true);
      hasProcessedRef.current = true;

      try {
        // Call API to find pack ID and resolve marketplaceId
        const result = await packsControllerFindPackByPartnerIds({
          collectionId: parsed.collectionId,
          packId: parsed.packId,
          instance: parsed.instance,
          telegramUserId: String(telegramUserId),
          mid: parsed.marketplaceId,
        });

        // Wait 1000ms before redirect to allow app to render
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (result.data.found && result.data.packId && result.data.marketplaceId) {
          // Redirect with packId and marketplaceId
          const redirectParameters = new URLSearchParams();
          redirectParameters.set('tab', 'stickers');
          redirectParameters.set('transferPackId', result.data.packId);
          redirectParameters.set('marketplaceId', result.data.marketplaceId);
          router.push(`${ROUTES.MY_COLLECTIONS}?${redirectParameters.toString()}`);
        } else {
          // Pack not found, redirect to my collections without transfer params
          router.push(ROUTES.MY_COLLECTIONS);
        }
      } catch {
        // On error, redirect to my collections without transfer params
        router.push(ROUTES.MY_COLLECTIONS);
      } finally {
        setIsHandlingTransfer(false);
      }
    };

    void handleTransfer();
  }, [router, enableBrowserTesting]);

  return { isHandlingTransfer };
};
